import { Filters } from "@/types/filters";
import type { BreadcrumbItem } from "@/utils/breadcrumb-utils";
import { filtersToUrl } from "@/utils/search-url-transform";
import { toSentenceCase } from "@/utils/string/to-sentence-case";
import { getCityCodeFromName } from "@/info/cities";

export const buildBreadcrumbItems = ({
	city,
	filters,
}: {
	city: string;
	filters: Partial<Filters>;
}): BreadcrumbItem[] => {
	// Get city code for URL construction
	// If city is already a code (lowercase), use it; otherwise convert from name
	const cityCode = city.toLowerCase() === city ? city : getCityCodeFromName(city) || city.toLowerCase();

	const breadcrumbItems: BreadcrumbItem[] = [
		{
			label: "Home",
			variant: "default",
			href: "/",
		},
		{
			label: city,
			variant: "city",
			href: `/${cityCode}`,
		},
	];

	const selectedLocation = filters.selectedLocationId
		? filters.locations?.find(location => location.id === filters.selectedLocationId)
		: filters.locations?.[0];

	// Add locality if found (comes after city)
	if (selectedLocation) {
		breadcrumbItems.push({
			label: selectedLocation.name,
			variant: "locality",
			href: `/${cityCode}/${filtersToUrl({ locations: [selectedLocation] })}`,
		});
	}

	// Add property type if available (comes after locality)
	if (filters.propertyType) {
		breadcrumbItems.push({
			label: toSentenceCase(filters.propertyType),
			variant: "propertyType",
			href: `/${cityCode}/${filtersToUrl({
				locations: selectedLocation ? [selectedLocation] : undefined,
				propertyType: filters.propertyType,
			})}`,
		});
	}

	// Add bedroom configuration if available (comes after property type)
	if (filters.bedrooms) {
		breadcrumbItems.push({
			label:
				filters.bedrooms.min === filters.bedrooms.max
					? `${filters.bedrooms.min}BHK`
					: `${filters.bedrooms.min}BHK - ${filters.bedrooms.max}BHK`,
			variant: "bedroom",
			href: `/${cityCode}/${filtersToUrl({
				locations: selectedLocation ? [selectedLocation] : undefined,
				propertyType: filters.propertyType,
				bedrooms: filters.bedrooms,
			})}`,
		});
	}

	return breadcrumbItems;
};
